import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function MasinaFirmaAngajatiPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/masina-firma-angajati")} />
      <ArticleLayout
        eyebrow="Gestiune flotă"
        title="Mașina de firmă folosită de angajați: cine completează foaia și ce documente trebuie"
        lead="Când angajatul conduce mașina firmei, foaia de parcurs nu dispare — doar se schimbă cine o semnează. Dar pe lângă foaie, trebuie și documente care arată că angajatul avea dreptul să folosească vehiculul. Inspectorul verifică ambele."
      >
        <p>
          În firmele cu un singur administrator, lucrurile sunt simple: una e mașina, una e
          persoana, una e foaia. Dar în momentul în care un <strong>angajat</strong> conduce
          vehiculul — fie pentru livrări, deplasări la clienți sau teren — apar întrebări:
          cine completează foaia, ce documente trebuie la predare și ce se întâmplă dacă
          același vehicul e folosit de mai multe persoane.
        </p>

        <h2>Cine completează foaia de parcurs</h2>
        <p>
          Foaia de parcurs se completează de <strong>persoana care conduce vehiculul</strong>
          în acea zi sau perioadă. Nu de contabil, nu de administrator (dacă nu el a condus),
          nu de cineva care «știe să facă foi».
        </p>
        <p>
          Practic, asta înseamnă:
        </p>
        <ul>
          <li>
            <strong>Administratorul conduce</strong> — el semnează foaia.
          </li>
          <li>
            <strong>Angajatul conduce</strong> — angajatul semnează foaia, iar firma păstrează
            un document care arată că avea dreptul să folosească mașina (delegație, fișă de
            predare sau menționare în regulamentul intern).
          </li>
          <li>
            <strong>Mai mulți șoferi pe același vehicul</strong> — fiecare semnează pentru
            deplasările lui, sau se ține o evidență pe ture/zile.
          </li>
        </ul>
        <p>
          Foaia poate fi completată de o singură persoană (de exemplu, un responsabil de
          flotă), dar <strong>semnătura conducătorului</strong> trebuie să apară. O foaie
          nesemnată sau semnată de altcineva decât cel care a condus e un semnal de alarmă
          la control.
        </p>

        <h2>Ce documente trebuie când angajatul folosește mașina</h2>
        <p>
          Foaia de parcurs arată unde a mers vehiculul. Dar inspectorul poate întreba și:
          «De ce a condus X și nu administratorul?» sau «Cu ce drept folosea angajatul
          mașina firmei?»
        </p>
        <p>
          Răspunsul vine din documente interne, nu din explicații verbale.
        </p>

        <h3>Delegație sau ordin de deplasare</h3>
        <p>
          Pentru deplasările punctuale (client, eveniment, livrare), delegația arată că
          angajatul a fost trimis de firmă într-o anumită misiune. Include:
        </p>
        <ul>
          <li>numele angajatului și funcția;</li>
          <li>destinația și scopul deplasării;</li>
          <li>perioada (date de plecare și întoarcere);</li>
          <li>vehiculul folosit (număr de înmatriculare);</li>
          <li>semnătura administratorului sau a persoanei care aprobă.</li>
        </ul>
        <p>
          Delegația nu e obligatorie prin lege pentru orice deplasare, dar e utilă ca
          document suport. La control, leagă angajatul de vehicul și de scopul economic.
        </p>

        <h3>Fișa de predare-primire a vehiculului</h3>
        <p>
          Dacă angajatul folosește mașina în mod regulat (zilnic sau săptămânal), o fișă de
          predare arată:
        </p>
        <ul>
          <li>data și ora predării;</li>
          <li>km la bord la predare;</li>
          <li>starea vehiculului (eventuale avarii, combustibil în rezervor);</li>
          <li>semnătura celui care predă și a celui care primește.</li>
        </ul>
        <p>
          Nu e un formular ANAF, ci un document intern. Scopul: să fie clar cine a avut
          mașina și în ce perioadă. E util mai ales când vehiculul trece de la un angajat
          la altul.
        </p>

        <h3>Menționarea în regulamentul intern</h3>
        <p>
          Dacă firma are un{" "}
          <Link to="/regulament-intern-autoturism">regulament intern auto</Link>, acolo se
          pot nominaliza angajații care au dreptul să conducă vehiculele firmei. Asta
          acoperă utilizarea curentă fără delegație pentru fiecare deplasare.
        </p>

        <h2>Mai mulți șoferi pe același vehicul</h2>
        <p>
          În firmele cu flotă mică, același vehicul poate fi folosit de mai mulți angajați —
          pe zile diferite, în ture sau în funcție de proiect. Foaia de parcurs trebuie să
          reflecte <strong>cine a condus și când</strong>.
        </p>
        <p>
          Opțiuni practice:
        </p>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Abordare</th>
                <th>Cum funcționează</th>
                <th>Când e potrivită</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Foaie separată per șofer</td>
                <td>Fiecare angajat completează foaia lui pentru zilele în care a condus.</td>
                <td>Șoferi care folosesc mașina în perioade distincte (săptămâni diferite).</td>
              </tr>
              <tr>
                <td>Foaie unică cu secțiune per șofer</td>
                <td>O singură foaie lunară, cu semnătura fiecărui conducător lângă traseele lui.</td>
                <td>Șoferi care alternează frecvent (zile diferite în aceeași săptămână).</td>
              </tr>
              <tr>
                <td>Registru de predare + foaie</td>
                <td>Un registru separat arată cine a preluat mașina; foaia rămâne unică.</td>
                <td>Flotă mai mare, cu predări zilnice între șoferi.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Important e să nu existe «găuri»: km care apar pe bord dar nu apar pe nicio foaie,
          sau perioade în care nu se știe cine a avut mașina.
        </p>

        <h2>Ce verifică ANAF când angajatul e pe foaie</h2>
        <p>
          Inspectorul nu respinge automat o foaie semnată de angajat. Dar verifică dacă
          există o logică internă:
        </p>
        <ul>
          <li>
            <strong>Angajatul lucrează în firmă?</strong> — contract de muncă activ pe
            perioada deplasărilor.
          </li>
          <li>
            <strong>Are sens ca el să conducă?</strong> — funcția lui justifică deplasările
            (agent vânzări, curier, tehnician).
          </li>
          <li>
            <strong>Există documente de atribuire?</strong> — delegație, regulament, fișă
            de predare.
          </li>
          <li>
            <strong>Traseele coincid cu activitatea firmei?</strong> — deplasări la clienți
            reali, nu curse personale mascate.
          </li>
        </ul>
        <p>
          Dacă administratorul e în concediu și foaia arată că tot el a condus, sau dacă
          angajatul a fost în concediu medical și totuși semnează trasee, apare o problemă
          de coerență.
        </p>

        <h2>Greșeli frecvente</h2>

        <h3>Foaie semnată de administrator, dar a condus angajatul</h3>
        <p>
          E tentant să semneze «șeful» pentru că «așa e mai simplu». Dar dacă inspectorul
          compară cu pontajul sau cu delegațiile, vede că administratorul era în altă parte.
        </p>

        <h3>Lipsă delegație pentru deplasări în afara localității</h3>
        <p>
          Angajatul merge la un client la 200 km, dar nu există niciun document care să
          arate că firma l-a trimis acolo. Deplasarea pare personală.
        </p>

        <h3>Mai mulți șoferi, o singură semnătură</h3>
        <p>
          Vehiculul a fost folosit de trei persoane într-o lună, dar foaia are o singură
          semnătură. Cine a făcut traseele din săptămâna 2?
        </p>

        <h3>Angajat fără contract activ</h3>
        <p>
          Persoana care semnează foaia nu apare în registrul de angajați pe acea perioadă.
          Cum conducea mașina firmei?
        </p>

        <div className="article-doc__callout">
          <strong>Regulă practică</strong>
          <p>
            Oricine conduce mașina firmei trebuie să apară undeva: în regulamentul intern,
            într-o delegație sau într-o fișă de predare. Foaia de parcurs confirmă doar ce
            s-a întâmplat — nu dă dreptul de a folosi vehiculul.
          </p>
        </div>

        <h2>Concluzie</h2>
        <p>
          Când angajatul conduce mașina firmei, foaia de parcurs rămâne obligatorie pentru
          100% — dar trebuie completată și semnată de cel care a condus efectiv. Pe lângă
          foaie, firma păstrează documente care arată de ce angajatul avea dreptul să
          folosească vehiculul: delegație, fișă de predare sau menționare în regulamentul
          intern.
        </p>
        <p>
          La control, inspectorul verifică coerența: angajatul exista, avea atribuții care
          justificau deplasările, iar km-ii de pe foaie se leagă de activitatea economică.
          Dacă totul se potrivește, subiectul e închis. Dacă nu — recalculare.
        </p>
        <RelatedGuides currentPath="/masina-firma-angajati" />
      </ArticleLayout>
    </>
  );
}
